import { useEffect, useRef, useState } from 'react';
import { createChart } from 'lightweight-charts';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const ProfessionalChart = ({ symbol, height = 500 }) => {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const [timeframe, setTimeframe] = useState('1h');
  const [loading, setLoading] = useState(true);

  const intervals = [
    { value: '1m', label: '1dk' },
    { value: '5m', label: '5dk' },
    { value: '15m', label: '15dk' },
    { value: '30m', label: '30dk' },
    { value: '1h', label: '1s' },
    { value: '4h', label: '4s' },
    { value: '1d', label: '1g' }
  ];

  useEffect(() => {
    if (!chartContainerRef.current) return;

    // Create chart
    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: height,
      layout: {
        background: { color: '#111827' },
        textColor: '#9ca3af',
      },
      grid: {
        vertLines: { color: '#1f2937' },
        horzLines: { color: '#1f2937' },
      },
      crosshair: {
        mode: 1,
      },
      rightPriceScale: {
        borderColor: '#374151',
      },
      timeScale: {
        borderColor: '#374151',
        timeVisible: true,
        secondsVisible: false,
      },
    });

    const candleSeries = chart.addCandlestickSeries({
      upColor: '#10b981',
      downColor: '#ef4444',
      borderUpColor: '#10b981',
      borderDownColor: '#ef4444',
      wickUpColor: '#10b981',
      wickDownColor: '#ef4444',
    });

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({ 
          width: chartContainerRef.current.clientWidth 
        });
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, [height]);

  useEffect(() => {
    if (!symbol || !candleSeriesRef.current) return;

    let isMounted = true;

    const fetchData = async () => {
      try {
        const response = await fetch(
          `${API}/binance/klines/${symbol}?interval=${timeframe}&limit=200`
        );
        const data = await response.json();

        if (isMounted && data.klines && data.klines.length > 0) {
          const formattedData = data.klines.map(kline => ({
            time: Math.floor(kline.openTime / 1000),
            open: kline.open,
            high: kline.high,
            low: kline.low,
            close: kline.close,
          }));

          candleSeriesRef.current.setData(formattedData);
          chartRef.current.timeScale().fitContent();
          setLoading(false);
        }
      } catch (error) {
        console.error('Grafik yüklenemedi:', error);
        setLoading(false);
      }
    };

    fetchData();
    const timer = setInterval(fetchData, 30000);

    return () => {
      isMounted = false;
      clearInterval(timer);
    };
  }, [symbol, timeframe]);

  return (
    <div className="w-full bg-gray-900 rounded-lg p-4">
      {/* Timeframe buttons */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
        {intervals.map((int) => (
          <button
            key={int.value}
            onClick={() => setTimeframe(int.value)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
              timeframe === int.value
                ? 'bg-blue-600 text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            {int.label}
          </button>
        ))}
      </div>

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 rounded-lg z-10">
          <div className="text-white">Yükleniyor...</div>
        </div>
      )}

      {/* Chart container */}
      <div ref={chartContainerRef} className="relative" />
    </div>
  );
};

export default ProfessionalChart;
